import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import moment from "moment";
import {Box, ButtonBase, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon, CallIcon, ViewIcon} from "@hugeicons/core-free-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import {SignInContext} from "../../context/SignInContext";
import {useWebrtc} from "../../context/WebrtcContext";
import {useFlowProtection} from "../../hooks/useFlowProtection";
import {useStompWebsocket} from "../../hooks/useStompWebsocket";
import {ConversationRepository} from "../../repositories/ConversationRepository";
import {AVATAR_COLORS, StatusBadge} from "../../pages/registry-project/dashboard/components/conversationUi";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {formatTime} from "../../utils/functions";

const PAGE_SIZE = 25;
const SCROLL_ID = "phone-calls-list";
// how far a row is swiped before it calls, and before a press stops counting as a click
const SWIPE_TRIGGER_PX = 96;
const SWIPE_START_PX = 8;

// One call. Pressing it opens the menu; swiping it right calls the customer again.
const CallRow = ({conversation, agent, canCall, onPress, onCall}) => {
  const [offset, setOffset] = useState(0);
  const gestureRef = useRef(null);
  const swipedRef = useRef(false);
  const name = conversation.senderName || conversation.sender;
  const created = moment(conversation.dateCreated);
  const avatar = AVATAR_COLORS[(Number(conversation.id) || 0) % AVATAR_COLORS.length];
  const duration = conversation.conversationDurationMillis;

  const onPointerDown = (e) => {
    swipedRef.current = false;
    if (canCall) gestureRef.current = {x: e.clientX, y: e.clientY, id: e.pointerId};
  };
  const onPointerMove = (e) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.id !== e.pointerId) return;
    const dx = e.clientX - gesture.x;
    if (!swipedRef.current) {
      // scrolling the list, not swiping this row
      if (Math.abs(e.clientY - gesture.y) > SWIPE_START_PX) gestureRef.current = null;
      if (!gestureRef.current || dx < SWIPE_START_PX) return;
      swipedRef.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    setOffset(Math.max(0, dx));
  };
  const endGesture = (e, cancelled) => {
    if (gestureRef.current?.id !== e.pointerId) return;
    gestureRef.current = null;
    if (!cancelled && offset >= SWIPE_TRIGGER_PX) onCall(conversation);
    setOffset(0);
  };

  return (
    <Box sx={{position: "relative", overflow: "hidden", borderBottom: "1px solid #F2F2F7"}}>
      <Stack direction={"row"} alignItems={"center"} gap={1} sx={{
        position: "absolute", inset: 0, pl: 2.5, fontSize: 13, fontWeight: 600,
        color: offset >= SWIPE_TRIGGER_PX ? "#fff" : "#1E8A55",
        bgcolor: offset >= SWIPE_TRIGGER_PX ? "#1E8A55" : "#E8F7EF",
      }}>
        <HugeiconsIcon icon={Call02Icon} size={18}/> Call again
      </Stack>
      <ButtonBase
        onClick={(e) => !swipedRef.current && onPress(e.currentTarget, conversation)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => endGesture(e, false)}
        onPointerCancel={(e) => endGesture(e, true)}
        sx={{
          width: "100%", display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1.25, textAlign: "left", bgcolor: "#fff",
          position: "relative", touchAction: "pan-y", userSelect: "none", transform: `translateX(${offset}px)`,
          transition: offset ? "none" : "transform 0.18s ease", "&:hover": {bgcolor: "#FAFAFD"},
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: avatar.bg, color: avatar.color,
        }}>
          <HugeiconsIcon icon={CallIcon} size={16}/>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}>
            <Typography noWrap sx={{fontSize: 14, fontWeight: 600, color: "#23264D"}}>{name || "Unknown"}</Typography>
            <Typography sx={{fontSize: 11, flexShrink: 0, color: "#8A8AA0"}}>
              {created.isSame(moment(), "day") ? created.format("HH:mm") : created.format("DD MMM")}
            </Typography>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{mt: 0.5, minWidth: 0}}>
            <StatusBadge conversation={conversation}/>
            <Typography noWrap sx={{fontSize: 11, color: "#8A8AA0"}}>
              {[duration ? formatTime(duration / 1000) : null, agent].filter(Boolean).join(" · ")}
            </Typography>
          </Stack>
        </Box>
      </ButtonBase>
    </Box>
  );
};

// The signed-in agent's AGENT_CALL conversations of the project, newest first, kept up to date from the project's
// conversations topic. A call is listed once a line of the agent has been stored, and stays when it is transferred.
export const PhoneCallsTab = ({onOpenConversation}) => {
  const {project, members} = useRegistryProject();
  const {currentUser} = useContext(SignInContext);
  const {status, call, callCustomer} = useWebrtc();
  const projectId = project?.id;
  const username = currentUser?.username;
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const requestRef = useRef(0);
  const [update] = useStompWebsocket(projectId ? `/topic/project/${projectId}/conversations` : null);
  // a new unlock token reloads the list: senders are masked without it
  const {token: protectionToken} = useFlowProtection();
  const canCall = status === "registered" && !call;

  const load = useCallback(async (pageToLoad) => {
    if (!projectId || !username) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const {data} = await ConversationRepository.getConversations({projectId, type: "AGENT_CALL", agent: username, page: pageToLoad, size: PAGE_SIZE});
      if (requestId !== requestRef.current) return;
      const items = data?.content || [];
      const ids = new Set(items.map(item => item.id));
      setTotal(data?.totalElements || 0);
      // the first page goes on top of the pages already scrolled (a pushed call reloads it)
      setConversations(prev => pageToLoad === 0
        ? [...items, ...prev.filter(item => !ids.has(item.id))]
        : [...prev.filter(item => !ids.has(item.id)), ...items]);
      if (pageToLoad > 0) setPage(pageToLoad);
    } catch (e) {
      console.error("Failed to load the calls", e);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, username, protectionToken]);

  useEffect(() => {
    load(0);
  }, [load]);

  // A pushed call: updated in place (its sender is always masked in a push), a new one reloads the first page
  useEffect(() => {
    if (!update?.id || update.type !== "AGENT_CALL") return;
    if (conversations.some(item => item.id === update.id)) {
      setConversations(list => list.map(item => item.id === update.id
        ? {...item, ...update, sender: item.sender, senderName: item.senderName ?? update.senderName}
        : item));
      return;
    }
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  const agentName = (username) => {
    const member = members?.find(item => item?.username === username);
    return [member?.name, member?.lastname].filter(Boolean).join(" ") || username;
  };
  // a live call is already connected to an agent
  const callable = (conversation) => canCall && conversation.status !== "ACTIVE";
  const callAgain = (conversation) => callCustomer({projectId, conversationId: conversation.id});

  return (
    <Box id={SCROLL_ID} sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
      {loading && conversations.length === 0 && (
        <Box sx={{display: "flex", justifyContent: "center", py: 5}}><CircularProgress size={20}/></Box>
      )}
      {!loading && conversations.length === 0 && (
        <Typography sx={{py: 5, px: 3, textAlign: "center", fontSize: 13, color: "#8A8AA0"}}>No calls yet</Typography>
      )}
      <InfiniteScroll
        dataLength={conversations.length}
        next={() => !loading && load(page + 1)}
        hasMore={conversations.length < total}
        loader={<Box sx={{display: "flex", justifyContent: "center", py: 2}}><CircularProgress size={18}/></Box>}
        scrollableTarget={SCROLL_ID}
      >
        {conversations.map(conversation => (
          <CallRow
            key={conversation.id}
            conversation={conversation}
            agent={conversation.monitoredBy ? agentName(conversation.monitoredBy) : null}
            canCall={callable(conversation)}
            onPress={(anchor, item) => setMenu({anchor, conversation: item})}
            onCall={callAgain}
          />
        ))}
      </InfiniteScroll>
      <Menu
        anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}
        anchorOrigin={{vertical: "center", horizontal: "center"}} transformOrigin={{vertical: "top", horizontal: "center"}}
        PaperProps={{sx: {borderRadius: "12px", minWidth: 200, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
      >
        <MenuItem onClick={() => {
          onOpenConversation(menu.conversation.id);
          setMenu(null);
        }} sx={{mx: 0.75, borderRadius: "8px"}}>
          <ListItemIcon sx={{minWidth: "28px !important"}}><HugeiconsIcon icon={ViewIcon} size={18}/></ListItemIcon>
          <ListItemText primary={"View conversation"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
        </MenuItem>
        <MenuItem disabled={!menu || !callable(menu.conversation)} onClick={() => {
          callAgain(menu.conversation);
          setMenu(null);
        }} sx={{mx: 0.75, borderRadius: "8px", color: "#1E8A55"}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#1E8A55"}}><HugeiconsIcon icon={Call02Icon} size={18}/></ListItemIcon>
          <ListItemText primary={"Call again"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
        </MenuItem>
      </Menu>
    </Box>
  );
};
